import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FAQ, { getFAQSchema } from '@/pages/home/components/FAQ';
import { useEffect } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const pagePath = '/hizmetler/seo';
const pageUrl = `${siteUrl}${pagePath}`;

const seoFAQItems = [
  {
    question: 'SEO nedir ve neden hala önemli?',
    answer:
      'SEO, markanızın arama motorlarında organik olarak görünür olmasını sağlayan stratejik bir disiplindir. Hala önemlidir çünkü kullanıcıların %68\'i online deneyimlerine arama motorlarıyla başlar. İyi bir SEO stratejisi, reklam bütçenizden bağımsız, sürdürülebilir bir trafik kaynağı oluşturur.',
  },
  {
    question: 'GEO nedir?',
    answer:
      'GEO (Generative Engine Optimization), markanızın ChatGPT, Perplexity, Gemini gibi yapay zeka tabanlı arama motorlarında doğru şekilde algılanmasını ve kaynak gösterilmesini sağlayan yeni nesil optimizasyon disiplinidir.',
  },
  {
    question: 'SEO ile GEO arasındaki fark nedir?',
    answer:
      'SEO, Google gibi geleneksel arama motorlarında sıralama için optimize eder. GEO ise yapay zeka asistanlarının markanızı nasıl anladığını, yorumladığını ve alıntıladığını yönetir. İkisi birbirini tamamlar: SEO otoritenizi inşa eder, GEO bu otoritenin AI tarafından doğru okunmasını sağlar.',
  },
  {
    question: 'SEO ne kadar sürede sonuç verir?',
    answer:
      'SEO uzun vadeli bir yatırımdır. Teknik iyileştirmeler genelde 1-2 ay içinde etkisini gösterir. İçerik stratejisi ve otorite inşası 3-6 ay arasında ivme kazanır. Rekabetçi sektörlerde tam potansiyele ulaşmak 6-12 ay sürebilir.',
  },
  {
    question: 'Sadece GEO yapmak yeterli mi?',
    answer:
      'Hayır. GEO, SEO\'nun üzerine inşa edilir. AI arama motorları markanız hakkında bilgiyi web sitenizden, üçüncü taraf kaynaklardan ve yapısal verilerinizden alır. Güçlü bir SEO altyapısı olmadan GEO tek başına etkili olamaz.',
  },
  {
    question: 'Hangi sektörler GEO\'ya daha fazla ihtiyaç duyar?',
    answer:
      'Özellikle B2B, danışmanlık, hukuk, sağlık, teknoloji ve finans gibi karar süreçlerinde araştırmanın yoğun olduğu sektörlerde GEO kritik önemdedir. Çünkü kullanıcılar artık bu sektörlerde AI asistanlarına doğrudan soru sorarak araştırma yapıyor.',
  },
];

const faqSchema = getFAQSchema(seoFAQItems);

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'SEO & GEO Hizmeti',
      provider: {
        '@type': 'Organization',
        name: 'BIFF Digital',
        url: siteUrl,
        telephone: '+905557101968',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17',
          addressLocality: 'Çankaya',
          addressRegion: 'Ankara',
          addressCountry: 'TR',
          postalCode: '06530',
        },
      },
      description:
        'SEO ve GEO hizmetleriyle markanızı hem geleneksel arama motorlarında hem yapay zeka asistanlarında otorite haline getiriyoruz. BIFF Digital, arama motoru optimizasyonunu strateji ve içerikle birlikte yönetir.',
      url: pageUrl,
      areaServed: { '@type': 'Country', name: 'Turkey' },
      serviceType: 'SEO & GEO',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'SEO & GEO Hizmet Detayları',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Teknik SEO', description: 'Site hızı, mobil uyumluluk, Core Web Vitals optimizasyonu' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Search Intent & İçerik Mimarisi', description: 'Arama niyetine dayalı içerik stratejisi ve sayfa mimarisi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Digital Authority', description: 'Otorite inşası ve backlink stratejisi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'GEO / AI Search Visibility', description: 'AI arama motorlarında görünürlük ve doğru algılanma' } },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Hizmetler', item: `${siteUrl}/hizmetler` },
        { '@type': 'ListItem', position: 3, name: 'SEO & GEO', item: pageUrl },
      ],
    },
    {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.speakable-summary'],
    },
    faqSchema,
  ],
};

export default function SeoPage() {
  useEffect(() => {
    document.title = 'SEO & GEO Ajansı | AI Search Visibility | BIFF Digital';

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    return () => {
      link?.setAttribute('href', siteUrl);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">

        {/* HERO */}
        <section className="section-padding pb-0 md:pb-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-4">
              HİZMETLER
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-50 tracking-tighter leading-none mb-8">
              SEO &amp; GEO
            </h1>
            <div className="h-px w-full bg-gradient-to-r from-white/15 via-white/5 to-transparent mb-10" />
            <p className="text-xl md:text-2xl text-foreground-200 font-medium leading-relaxed mb-6">
              Aranan Değil,<br />
              <span className="text-primary-500">Bulunan Marka Olun.</span>
            </p>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-2xl speakable-summary">
              Google&apos;da ilk sayfada çıkmak yetmez. Artık markanızın ChatGPT, Perplexity ve Gemini gibi yapay zeka asistanlarında da doğru algılanması gerekir. BIFF Digital olarak SEO ve GEO&apos;yu tek bir stratejik çerçevede yönetiyoruz.
            </p>
          </div>
        </section>

        {/* SEO Sıralama Değildir. Otoritedir. */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              SEO Sıralama Değildir.<br />Otoritedir.
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6">
              Sıralama bir sonuçtur. Asıl hedef, markanızın sektöründe referans noktası haline gelmesidir. Bir kullanıcı arama yaptığında markanızı görsün diye değil, markanızı arasın diye çalışıyoruz.
            </p>
            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500">
              <p className="text-base md:text-lg text-foreground-100 font-medium leading-relaxed">
                Google&apos;a değil, insana yazıyoruz.<br />
                <strong className="text-foreground-50">Çünkü Google da insan gibi okumayı öğrendi.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* GEO Neden Önemli */}
        <section className="section-padding bg-background-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              GEO Neden Önemli?
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-8">
              Artık kullanıcılar Google&apos;a yazdıkları soruyu ChatGPT&apos;ye de soruyor. Perplexity&apos;de araştırma yapıyor. Gemini&apos;dan öneri alıyor. Eğer markanız bu platformlarda doğru algılanmıyorsa, görünmezsiniz.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              {[
                { stat: '%45', label: 'Kullanıcıların AI asistanlarını araştırma için kullanma oranı' },
                { stat: '%62', label: 'ChatGPT\'nin kaynak gösterdiği markaların SEO otoritesi yüksek' },
                { stat: '3x', label: 'GEO optimize içeriklerin AI alıntılanma olasılığı' },
              ].map((item, i) => (
                <div key={i} className="glass-card rounded-2xl p-6 text-center">
                  <p className="text-3xl md:text-4xl font-black text-primary-500 font-heading mb-2">{item.stat}</p>
                  <p className="text-xs text-foreground-400 leading-relaxed">{item.label}</p>
                </div>
              ))}
            </div>

            <div className="glass-card rounded-2xl p-6 md:p-8">
              <p className="text-sm text-foreground-300 leading-relaxed">
                GEO, markanızın yapay zeka tarafından <strong className="text-foreground-50">doğru anlaşılmasını</strong> sağlar. Yanlış anlaşılmak, görünmez olmaktan daha tehlikelidir.
              </p>
            </div>
          </div>
        </section>

        {/* Ne Yapıyoruz */}
        <section className="section-padding">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">
                NE YAPIYORUZ?
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter">
                Arama Motorlarında ve AI&apos;da<br />Otorite İnşası
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              {[
                {
                  icon: 'ri-code-s-slash-line',
                  title: 'Teknik SEO',
                  desc: 'Site hızı, mobil uyumluluk, Core Web Vitals, yapısal veri ve tarama bütçesi optimizasyonuyla arama motorlarının sitenizi hatasız okumasını sağlıyoruz.',
                },
                {
                  icon: 'ri-search-eye-line',
                  title: 'Search Intent & İçerik Mimarisi',
                  desc: 'Kullanıcının arama niyetini analiz ediyor, her arama amacına uygun içerik ve sayfa mimarisi kuruyoruz. Bilgi, karşılaştırma, satın alma niyetlerini ayrı ayrı ele alıyoruz.',
                },
                {
                  icon: 'ri-file-text-line',
                  title: 'On-Page SEO',
                  desc: 'Başlık hiyerarşisi, meta etiketleri, iç linkleme, semantik HTML ve E-E-A-T sinyalleriyle her sayfanın arama motorları tarafından doğru sınıflandırılmasını sağlıyoruz.',
                },
                {
                  icon: 'ri-shield-star-line',
                  title: 'Digital Authority',
                  desc: 'Backlink profili analizi, dijital PR, sektörel otorite inşası ve güven sinyalleriyle markanızın Google gözündeki otoritesini sistematik olarak yükseltiyoruz.',
                },
              ].map((item, i) => (
                <div key={i} className="glass-card-hover rounded-2xl p-6 md:p-8 group">
                  <div className="w-12 h-12 bg-background-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500/10 transition-colors duration-300">
                    <i className={`${item.icon} text-xl text-primary-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-50 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* GEO / AI Search Visibility */}
        <section className="section-padding bg-background-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              GEO / AI Search Visibility
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-8">
              AI arama motorları markanızı web sitenizden, yapısal verilerinizden, üçüncü taraf kaynaklardan ve içeriklerinizden okuyarak anlar. GEO ile bu okumanın doğru olmasını sağlıyoruz.
            </p>

            <div className="space-y-4">
              {[
                { step: '01', title: 'Entity Tanımlama', desc: 'Markanızın AI sistemlerinde bir entity olarak doğru tanımlanmasını sağlıyoruz.' },
                { step: '02', title: 'Yapısal Veri Optimizasyonu', desc: 'Schema.org işaretlemeleriyle AI\'ın markanız hakkında net, yapılandırılmış bilgi almasını sağlıyoruz.' },
                { step: '03', title: 'Speakable & Alıntılanabilir İçerik', desc: 'AI asistanlarının doğrudan alıntılayabileceği, net tanımlar ve soru-cevap yapıları oluşturuyoruz.' },
                { step: '04', title: 'Çapraz Platform Tutarlılığı', desc: 'Web sitesi, sosyal medya, sektörel platformlar ve dizinlerde marka bilgilerinizin tutarlı olmasını sağlıyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card rounded-2xl p-5 md:p-6 flex items-start gap-5">
                  <span className="text-2xl font-black text-primary-500 font-heading shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-base font-bold text-foreground-100 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Google İçin Değil, İnsan İçin */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              Google İçin Değil,<br />İnsan İçin Yazıyoruz
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6">
              2010&apos;ların SEO taktikleri artık çalışmıyor. Anahtar kelime yoğunluğu hesaplamak, backlink satın almak, yapay içeriklerle sıralama kovalamak — bunlar sadece işe yaramıyor, markanıza zarar veriyor.
            </p>
            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500 mb-8">
              <p className="text-base md:text-lg text-foreground-50 font-bold leading-relaxed mb-3">
                Google, 2024&apos;ten beri insan gibi okuyor.
              </p>
              <p className="text-sm text-foreground-300 leading-relaxed">
                Helpful Content Update, Core Update ve E-E-A-T sinyalleriyle Google, içeriğin gerçekten faydalı olup olmadığını, yazarın uzman olup olmadığını, sitenin güvenilir olup olmadığını değerlendiriyor. Biz de tam olarak bunu inşa ediyoruz: gerçek uzmanlık, gerçek deneyim, gerçek otorite.
              </p>
            </div>
          </div>
        </section>

        {/* BIFF SEO Metodolojisi */}
        <section className="section-padding bg-background-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-10">
              BIFF SEO Metodolojisi
            </h2>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Audit', desc: 'Mevcut durum analizi. Teknik altyapı, içerik envanteri, backlink profili, rakip analizi ve arama niyeti haritası çıkarıyoruz.' },
                { step: '02', title: 'Strateji', desc: 'Hedef sektör, hedef kitle ve arama niyetlerine göre içerik mimarisi ve otorite inşa planı oluşturuyoruz. Hangi sayfa hangi niyete cevap verecek — tek tek belirliyoruz.' },
                { step: '03', title: 'İçerik ve Teknik Uygulama', desc: 'İçerikleri BIFF editör ekibiyle üretiyor, teknik SEO iyileştirmelerini development ekibiyle uyguluyoruz. İçerik ve teknik aynı elden çıkıyor.' },
                { step: '04', title: 'GEO Entegrasyonu', desc: 'Tüm içeriklere yapısal veri, speakable işaretleme ve AI-okunabilir yapı ekliyoruz. Sayfa yayına girdiği andan itibaren GEO uyumlu oluyor.' },
                { step: '05', title: 'Ölçüm ve Optimizasyon', desc: 'Organik trafik, sıralama, tıklama oranı ve AI alıntılanma metriklerini sürekli izliyor, stratejiyi veriye dayalı olarak güncelliyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card rounded-2xl p-5 md:p-6 flex items-start gap-5">
                  <span className="text-2xl font-black text-primary-500 font-heading shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-base font-bold text-foreground-100 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ
          items={seoFAQItems}
          sectionTitle="SIK SORULANLAR"
          sectionSubtitle="SEO &amp; GEO"
          sectionSummary="SEO ve GEO hakkında en sık sorulan soruları, doğrudan ve net cevaplarla burada topladık."
          speakableClass={false}
        />

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-bl-full -mr-16 -mt-16" />
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 tracking-tighter mb-4">
                  Markanızın arandığı yeri değil,<br />
                  <span className="text-primary-500">karar verilen yeri</span> hedefleyelim.
                </h2>
                <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto leading-relaxed">
                  İlk SEO/GEO audit&apos;inizi ücretsiz yapalım. Markanız şu an arama motorlarında ve AI asistanlarında nasıl görünüyor, birlikte bakalım.
                </p>
                <a href="/iletisim" className="btn-primary text-base px-8 py-4">
                  Ücretsiz SEO/GEO Analizi
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}